import { useComplimentaryTrialEndDevControls } from '@heidi/commerce';
import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import {
  TypographyCaption,
  TypographyCaptionBold,
  TypographyP2SemiBold,
} from '@heidi/design-system/typography';

const DAY_OPTIONS = [7, 6, 5, 4, 3, 2, 1] as const;

interface ComplimentaryTrialEndDevSectionProps {
  /** Called after an action that should close the dev-tools sheet. */
  onRequestClose?: () => void;
}

/**
 * Dev-tools section for the last week of complimentary Heidi 2 Max usage: force
 * a day count and cohort, reopen the T-7 modal and bring the banner back.
 */
export const ComplimentaryTrialEndDevSection = ({
  onRequestClose,
}: ComplimentaryTrialEndDevSectionProps) => {
  const { forced, force, clear, openModal, resetBannerDismissal } =
    useComplimentaryTrialEndDevControls();
  const daysLeft = forced?.daysLeft ?? 7;
  const cohort = forced?.cohort ?? 'active';

  return (
    <section aria-labelledby="dev-tools-trial-end-heading" className="mt-6">
      <TypographyP2SemiBold id="dev-tools-trial-end-heading" className="mb-2">
        Heidi 2 complimentary usage end
      </TypographyP2SemiBold>
      <div className="flex flex-col gap-3">
        <TypographyCaption className="text-text-tertiary">
          Forces the last-week countdown without a real allowance, flag or opt-in, so the
          banner and T-7 modal show anywhere. The sidebar tile still needs a Heidi 2
          opt-in account with release-heidi-2-opt-in on. Per-tab; ignored in production.
        </TypographyCaption>

        <div className="flex items-center justify-between gap-3">
          <TypographyCaption className="text-text-secondary">
            Active override
          </TypographyCaption>
          {forced ? (
            <div className="flex items-center gap-2">
              <Badge size="xs" color="green">
                {forced.cohort} · {forced.daysLeft}d left
              </Badge>
              <Button variant="ghost" size="xxs" onClick={clear}>
                Clear
              </Button>
            </div>
          ) : (
            <TypographyCaption className="text-text-tertiary">
              none — real allowance
            </TypographyCaption>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <TypographyCaptionBold>Cohort</TypographyCaptionBold>
          <div className="flex gap-2">
            <Button
              variant={forced && cohort === 'active' ? 'default' : 'secondary'}
              size="xs"
              onClick={() => force({ daysLeft, cohort: 'active' })}
            >
              Active (≥50%)
            </Button>
            <Button
              variant={forced && cohort === 'inactive' ? 'default' : 'secondary'}
              size="xs"
              onClick={() => force({ daysLeft, cohort: 'inactive' })}
            >
              Inactive (&lt;50%)
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <TypographyCaptionBold>Days left</TypographyCaptionBold>
          <div className="flex flex-wrap gap-1.5">
            {DAY_OPTIONS.map((days) => (
              <Button
                key={days}
                variant={forced && daysLeft === days ? 'default' : 'secondary'}
                size="xxs"
                onClick={() => force({ daysLeft: days, cohort })}
              >
                {days}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="xs"
            onClick={() => {
              if (!forced) force({ daysLeft, cohort });
              openModal('side_nav');
              onRequestClose?.();
            }}
          >
            Open T-7 modal
          </Button>
          <Button variant="ghost" size="xs" onClick={resetBannerDismissal}>
            Reset banner dismissal
          </Button>
        </div>
      </div>
    </section>
  );
};
